import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Alert, AlertDescription } from './ui/alert';
import { ArrowLeft, Loader2, ExternalLink, CreditCard, CheckCircle } from 'lucide-react';
import type { User } from '../App';
import {
  getFilingDocPrepEligibility,
  beginFilingDocPrepCheckout,
  redeemFilingDocPrepVoucher,
  type FilingDocPrepEligibility,
} from '../api/billing';
import { getSubmission } from '../api/innovations';

interface InnovatorFilingDocPaystackPageProps {
  user: User;
  onBack: () => void;
}

type PollUi = 'idle' | 'waiting' | 'confirmed' | 'timeout';

const POLL_MS = 4000;
const MAX_POLLS = 45;

export function InnovatorFilingDocPaystackPage({ user, onBack }: InnovatorFilingDocPaystackPageProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [eligibility, setEligibility] = useState<FilingDocPrepEligibility | null>(null);
  const [selectedUuid, setSelectedUuid] = useState<string>('');
  const [payLoading, setPayLoading] = useState(false);
  const [pollUi, setPollUi] = useState<PollUi>('idle');
  const [voucherCode, setVoucherCode] = useState('');
  const [voucherBusy, setVoucherBusy] = useState(false);
  const [voucherError, setVoucherError] = useState<string | null>(null);
  const pollAttempts = useRef(0);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getFilingDocPrepEligibility();
        if (!cancelled) {
          setEligibility(data);
          if (data.submissions.length === 1) {
            setSelectedUuid(data.submissions[0].submission_uuid);
          }
        }
      } catch (e: unknown) {
        if (!cancelled) {
          const msg =
            e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : 'Failed to load';
          setError(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
  }, []);

  const stopPolling = () => {
    if (pollTimer.current) {
      clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
    pollAttempts.current = 0;
  };

  const schedulePoll = (uuid: string) => {
    const tick = async () => {
      pollAttempts.current += 1;
      try {
        const sub = await getSubmission(uuid);
        if (sub.filing_documentation_prep?.billing_cleared_at) {
          stopPolling();
          setPollUi('confirmed');
          return;
        }
      } catch {
        /* keep polling */
      }
      if (pollAttempts.current >= MAX_POLLS) {
        stopPolling();
        setPollUi('timeout');
        return;
      }
      pollTimer.current = setTimeout(() => void tick(), POLL_MS);
    };
    void tick();
  };

  const refreshEligibility = async () => {
    const data = await getFilingDocPrepEligibility();
    setEligibility(data);
    const still = data.submissions.some((s) => s.submission_uuid === selectedUuid);
    if (!still) {
      setSelectedUuid(data.submissions.length === 1 ? data.submissions[0].submission_uuid : '');
    }
  };

  const handleRedeemVoucher = async (e: React.FormEvent) => {
    e.preventDefault();
    setVoucherError(null);
    const code = voucherCode.trim();
    if (code.length < 4) {
      setVoucherError('Enter a voucher code (at least 4 characters).');
      return;
    }
    if (!selectedUuid) {
      setVoucherError('Select an innovation submission first.');
      return;
    }
    setVoucherBusy(true);
    stopPolling();
    setPollUi('idle');
    try {
      await redeemFilingDocPrepVoucher(code, selectedUuid);
      setVoucherCode('');
      setPollUi('confirmed');
      await refreshEligibility();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string } }; message?: string };
      setVoucherError(ax.response?.data?.message || ax.message || 'Could not redeem voucher.');
    } finally {
      setVoucherBusy(false);
    }
  };

  const handlePay = async () => {
    if (!selectedUuid) {
      setError('Select an innovation submission first.');
      return;
    }
    setPayLoading(true);
    setError(null);
    setPollUi('idle');
    stopPolling();
    try {
      const res = await beginFilingDocPrepCheckout(selectedUuid);
      const url = res.payment_page_url;
      if (!url) {
        setError('No Paystack payment page URL was returned.');
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
      setPollUi('waiting');
      schedulePoll(selectedUuid);
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : 'Payment start failed';
      setError(msg);
    } finally {
      setPayLoading(false);
    }
  };

  const fmtMoney = () => {
    if (!eligibility) return '';
    const { amount_major: major, currency } = eligibility;
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(major);
    } catch {
      return `${major} ${currency}`;
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      <Button variant="ghost" onClick={onBack} className="text-[var(--navy)]">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to dashboard
      </Button>

      <Card className="border-[rgba(201,168,76,0.2)]">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[var(--navy)] font-display">
            <CreditCard className="w-6 h-6 text-[var(--gold)]" />
            Filing documentation — payment or voucher
          </CardTitle>
          <CardDescription className="text-[var(--navy)]/80">
            Pay the filing-documentation preparation fee on your Insolip Paystack Payment Page, or apply a promotional
            voucher for the <strong>selected submission</strong>. Use the <strong>same email</strong> as your Insolip account (
            {user.email}) on Paystack so the webhook can match your payment.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="w-8 h-8 animate-spin text-[var(--gold)]" />
            </div>
          ) : error && !eligibility ? (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : (
            <>
              {eligibility && !eligibility.payment_page_configured && (
                <Alert variant="destructive">
                  <AlertDescription>
                    This environment is missing <code className="text-xs">PAYSTACK_FILING_DOC_PREP_PAGE_URL</code> on the
                    server (the static Paystack Payment Page URL for this fee, like the innovator assessment page). Ask your
                    administrator to set it in the backend <code className="text-xs">.env</code>.
                  </AlertDescription>
                </Alert>
              )}
              {eligibility && eligibility.submissions.length === 0 ? (
                <p className="text-[var(--navy)]/80 text-sm">
                  You have no submissions that need this payment right now. Eligible rows are: your own individual
                  (non-institution) submissions, assessment <strong>completed</strong>, feedback <strong>approved</strong>,
                  filing documentation <strong>not yet requested</strong>, and billing <strong>not yet recorded</strong>.
                </p>
              ) : (
                <>
                  <div className="rounded-lg border border-slate-200 bg-slate-50/80 p-4 text-sm text-slate-800 space-y-1">
                    <p>
                      <strong>Configured amount (must match the Paystack page):</strong> {fmtMoney()} ({eligibility?.currency})
                    </p>
                    <p className="text-slate-600">
                      The webhook matches your payment using <code className="text-xs bg-white px-1 rounded">PAYSTACK_FILING_DOC_AMOUNT</code>{' '}
                      and <code className="text-xs bg-white px-1 rounded">PAYSTACK_FILING_DOC_CURRENCY</code>. Use a{' '}
                      <strong>different</strong> fixed amount than your innovator assessment Payment Page so charges are never
                      mixed up.
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="filing-sub-pick" className="text-[var(--navy)]">
                      Innovation submission
                    </Label>
                    <select
                      id="filing-sub-pick"
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm bg-white text-slate-900"
                      value={selectedUuid}
                      onChange={(e) => setSelectedUuid(e.target.value)}
                    >
                      <option value="">Select a submission…</option>
                      {(eligibility?.submissions || []).map((s) => (
                        <option key={s.submission_uuid} value={s.submission_uuid}>
                          {s.title}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="rounded-lg border border-[rgba(201,168,76,0.35)] bg-white/80 p-4 space-y-3">
                    <p className="text-sm font-medium text-[var(--navy)]">Have a filing-documentation voucher?</p>
                    <p className="text-sm text-slate-600">
                      Codes are issued separately from the innovator assessment voucher. Each code can only be used once per
                      account and applies to the submission you select above.
                    </p>
                    <form onSubmit={handleRedeemVoucher} className="flex flex-col sm:flex-row gap-2 sm:items-end">
                      <div className="flex-1 space-y-1">
                        <Label htmlFor="filing-doc-voucher-code" className="text-slate-700">
                          Voucher code
                        </Label>
                        <Input
                          id="filing-doc-voucher-code"
                          type="text"
                          autoComplete="off"
                          placeholder="e.g. INSOLIP-FILING-2026"
                          value={voucherCode}
                          onChange={(e) => setVoucherCode(e.target.value)}
                          disabled={voucherBusy}
                          className="uppercase"
                        />
                      </div>
                      <Button type="submit" variant="secondary" disabled={voucherBusy || !selectedUuid} className="shrink-0">
                        {voucherBusy ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Applying…
                          </>
                        ) : (
                          'Apply voucher'
                        )}
                      </Button>
                    </form>
                    {voucherError && (
                      <p className="text-sm text-red-600" role="alert">
                        {voucherError}
                      </p>
                    )}
                  </div>

                  {error && (
                    <Alert variant="destructive">
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  {pollUi === 'waiting' && (
                    <Alert className="border-sky-200 bg-sky-50">
                      <AlertDescription className="text-slate-800 text-sm flex items-start gap-2">
                        <Loader2 className="w-4 h-4 mt-0.5 shrink-0 animate-spin text-sky-700" />
                        <span>
                          Complete payment in the Paystack tab. This page will detect when billing is recorded (usually a few
                          seconds). You can leave this tab open.
                        </span>
                      </AlertDescription>
                    </Alert>
                  )}

                  {pollUi === 'confirmed' && (
                    <Alert className="border-emerald-300 bg-emerald-50">
                      <AlertDescription className="text-emerald-950 text-sm flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 mt-0.5 shrink-0 text-emerald-700" />
                        <span>
                          <strong>Payment recorded.</strong> You can request filing documentation preparation from your
                          dashboard for this submission. If the action does not appear yet, refresh the dashboard.
                        </span>
                      </AlertDescription>
                    </Alert>
                  )}

                  {pollUi === 'timeout' && (
                    <Alert variant="destructive">
                      <AlertDescription>
                        We could not confirm billing automatically yet. If Paystack charged you, wait a minute and refresh your
                        dashboard, or contact support with your Paystack reference.
                      </AlertDescription>
                    </Alert>
                  )}

                  <Alert className="bg-[var(--gold-pale)] border-[rgba(201,168,76,0.35)]">
                    <AlertDescription className="text-[var(--navy)] text-sm space-y-2">
                      <p>
                        <strong>Webhook:</strong> your server must expose{' '}
                        <code className="text-xs break-all">POST /api/v1/billing/webhooks/paystack</code> with{' '}
                        <code className="text-xs">PAYSTACK_SECRET_KEY</code> so Insolip can unlock &quot;request filing
                        documentation&quot; automatically after Paystack confirms the charge.
                      </p>
                    </AlertDescription>
                  </Alert>

                  <Button
                    className="w-full bg-[var(--gold)] text-[var(--navy)] hover:opacity-90"
                    disabled={
                      payLoading ||
                      !selectedUuid ||
                      !eligibility?.payment_page_configured ||
                      eligibility.submissions.length === 0 ||
                      pollUi === 'waiting'
                    }
                    onClick={handlePay}
                  >
                    {payLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <ExternalLink className="w-4 h-4 mr-2" />
                    )}
                    {payLoading ? 'Starting checkout…' : 'Open Paystack in a new tab'}
                  </Button>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
